import { formatTimeZoneOffset, getTimeZoneName } from '@chronosphereio/core';
import { UseTimeZoneDetailsResult } from '../DateTimeRangePicker/utils/useTimeZoneDetails';

export const USER_TIME_ZONE_ID = 'userTimeZone';

export type TimeZoneOption = {
  id: string;
  group: string;
  title: string;
  subtitle?: string;
  offset: string;
};

/*
  Browsers we target support recently added Intl features:
    https://caniuse.com/mdn-javascript_builtins_intl_datetimeformat_resolvedoptions
    https://caniuse.com/mdn-javascript_builtins_intl_supportedvaluesof

  The latest version of TS does not include definitions for these:
    https://github.com/microsoft/TypeScript/issues/49231

  Until that issue is resolved, we extend the Intl namespace definition
*/
// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace Intl {
  type Key = 'calendar' | 'collation' | 'currency' | 'numberingSystem' | 'timeZone' | 'unit';
  function supportedValuesOf(input: Key): string[];
  function DateTimeFormat(
    locale?: string,
    options?: { timeZone?: string; timeZoneName?: string }
  ): {
    resolvedOptions: () => { timeZone: string };
    format: (date: Date) => string;
  };
}

export function loadSupportedTimeZones() {
  // Intl.supportedValuesOf is not defined in jsdom, which will break unit tests
  // fallback to empty array in that case
  const supportedTimeZones = Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone') : [];

  return supportedTimeZones
    .sort((a, b) => -b.localeCompare(a))
    .map((id) => ({
      id,
      abbreviation: getTimeZoneName(id, {
        variant: 'short',
      }),
      offset: formatTimeZoneOffset(id),
    }));
}

type TimeZoneInfo = { id: string; abbreviation?: string; offset: string };

function getBrowserTimeZoneSubtitle(browserTimeZone?: TimeZoneInfo) {
  if (!browserTimeZone) {
    return '';
  }

  // In theory, the abbreviation can be undefined, so we do a little handling to ensure there's only a
  // comma here when we have two parts to join. This is likely a rare edge case.
  const nonEmptySubtitleParts = [browserTimeZone.id, browserTimeZone.abbreviation].filter((part) => part);
  return nonEmptySubtitleParts.join(', ');
}

interface LoadTimeZoneOptions {
  supportedTimeZones: TimeZoneInfo[];
  browserTimeZoneId: string;
  userTimeZoneDetails?: UseTimeZoneDetailsResult;
}
export function loadTimeZoneOptions({
  userTimeZoneDetails,
  supportedTimeZones,
  browserTimeZoneId,
}: LoadTimeZoneOptions) {
  const timeZoneOptionList: TimeZoneOption[] = [];
  const timeZoneOptionDictionary: Record<string, TimeZoneOption> = {};

  // account time zone
  if (userTimeZoneDetails) {
    const { IANA, shortName, longUTCOffset } = userTimeZoneDetails;
    const userTimeZoneOption: TimeZoneOption = {
      id: USER_TIME_ZONE_ID,
      group: '',
      title: 'Account time zone',
      subtitle: `${IANA}, ${shortName}`,
      offset: longUTCOffset,
    };
    timeZoneOptionList.push(userTimeZoneOption);
    timeZoneOptionDictionary[userTimeZoneOption.id] = userTimeZoneOption;
  }

  // browser selected timezone
  const browserTimeZone = supportedTimeZones.find((timeZone) => timeZone.id === browserTimeZoneId);

  const localTimeZoneOption: TimeZoneOption = {
    id: 'local',
    group: '',
    title: 'Local time',
    subtitle: getBrowserTimeZoneSubtitle(browserTimeZone),
    offset: browserTimeZone ? browserTimeZone.offset : '',
  };
  timeZoneOptionList.push(localTimeZoneOption);
  timeZoneOptionDictionary[localTimeZoneOption.id] = localTimeZoneOption;

  // UTC timezone
  const utcTimeZoneOption: TimeZoneOption = {
    id: 'UTC',
    group: '',
    title: 'Coordinated Universal Time',
    subtitle: 'GMT',
    offset: 'UTC',
  };
  timeZoneOptionList.push(utcTimeZoneOption);
  timeZoneOptionDictionary[utcTimeZoneOption.id] = utcTimeZoneOption;

  // IANA timezones supported by the browser
  supportedTimeZones.forEach((timeZone) => {
    const option: TimeZoneOption = {
      id: timeZone.id,
      group: timeZone.id.split('/')[0] ?? 'Unknown group',
      title: timeZone.id,
      subtitle: timeZone.abbreviation,
      offset: timeZone.offset,
    };
    timeZoneOptionList.push(option);
    timeZoneOptionDictionary[option.id] = option;
  });

  return { timeZoneOptionList, timeZoneOptionDictionary };
}
